import * as React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useGetAllFeeQuery } from '../store/feeApi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import ViewFee from './Fees/ViewFee';
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";

const ViewFeesComponent = () => {
  const [studentName, setStudentName] = React.useState('');
  const [selectedMonth, setSelectedMonth] = React.useState('');
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('all');

  const { user } = useSelector((state: RootState) => state.user);
  const { data: fees } = useGetAllFeeQuery(user?.token);


  // Format the selected date to 'dd/MM/yyyy' to match your display and filtering
  const formattedSelectedDate = selectedDate
    ? format(selectedDate, 'dd/MM/yyyy')  // e.g., "20/12/2025"
    : '';

  // Also format for display in button
  const displayDate = selectedDate
    ? format(selectedDate, 'dd MMM yyyy')  // e.g., "20 Dec 2025"
    : null;

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = ['2027', '2026', '2025', '2024', '2023'];

  const handleClearSearch = () => {
    setStudentName('');
    setSelectedDate('');
    setSelectedMonth('');
    setSelectedYear('');
    setSelectedStatus('all');
  };

  return (
    <div className="min-h-screen bg-background p-6 overflow-x-hidden">
      <div className="w-full mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">View Fees</h1>
          <div className="text-sm text-muted-foreground">
            <span>Dashboard</span>
            <span className="mx-2">/</span>
            <span>View Fees</span>
          </div>
        </div>

        <Card className="bg-white overflow-x-auto md:overflow-x-visible">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Name filter */}
              {user.role==='hr' &&
              <div className="col-span-1">
                <Input
                  placeholder="Enter Student Name"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  className="w-full"
                />
              </div>
              }

              {/* Date picker */}
              <div className="col-span-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {displayDate ? displayDate : <span className="text-muted-foreground">Select Date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Month selector */}
              <div className="col-span-1">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Year selector */}
              <div className="col-span-1">
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status selector */}
              <div className="col-span-1">
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="due">Due</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-1 flex gap-2">
                <Button className="flex-1 bg-green-600 hover:bg-green-700">
                  Search
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleClearSearch}
                >
                  Clear Search
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ViewFee
          fees={fees}
          selectedMonth={selectedMonth}
          selectedDate={formattedSelectedDate}
          selectedYear={selectedYear}
          studentName={studentName}
          selectedStatus={selectedStatus}
        />
      </div>
    </div>
  );
};

export default ViewFeesComponent;
